import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Check,
  Mail,
  Phone,
  ShieldCheck,
} from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import BusinessAssistancePromo from "@/components/assistance/BusinessAssistancePromo";

// ─── Assets extracted from the approved FINAL design PDF ───
const LOGO = "/assets/logo.png";
const ABOUT_LOGO = "/assets/logo.jpg";
const HERO_IMG = "/assets/hero-showcase.jpg";

const GALLERY = [
  { img: "/assets/before-card.jpg",            title: "Before — Original Facebook Branding",  caption: "Original Facebook branding" },
  { img: "/assets/after-card.jpg",             title: "After — Refreshed Facebook Branding",  caption: "Refreshed Facebook branding" },
  { img: "/assets/work-logo-design.jpg",     title: "Logo Design",                         caption: "The Toliver Signature Collection" },
  { img: "/assets/work-branding.jpg",        title: "Branding & Rebranding",               caption: "The Toliver Signature Collection" },
  { img: "/assets/work-print-marketing.jpg", title: "Print & Marketing",                   caption: "The Toliver Signature Collection" },
  { img: "/assets/work-video-ads.jpg",       title: "Video Ads",                           caption: "The Toliver Signature Collection" },
  { img: "/assets/work-business-docs.jpg",   title: "Business Documents",                  caption: "The Toliver Signature Collection" },
  { img: "/assets/work-creative-range.jpg",  title: "Creative Range",                      caption: "The Toliver Signature Collection" },
  { img: "/assets/site-before.jpg",          title: "Before — Original Website Design",    caption: "Website refresh example" },
  { img: "/assets/site-after.jpg",           title: "After — Refreshed Website Design",    caption: "Website refresh example" },
  { img: "/assets/new-website-example.jpg",  title: "New Website — Built From Scratch",    caption: "Maison Luxe Events — new website example" },
];
const PORTFOLIO = GALLERY.slice(2, 8);

// ─── Data (exact approved content & pricing from the PDF) ──
const SERVICE_CATEGORIES = [
  {
    title: "Logo Design Packages",
    desc: "Custom logos and brand guidelines that make your business easy to recognize.",
    items: [
      { n: "Essential Logo", p: "$45", f: ["1 text-based logo concept", "1 revision", "High-resolution PNG"] },
      { n: "Starter Logo", p: "$85", f: ["1 logo concept with text and a simple icon", "2 revisions", "High-resolution PNG"] },
      { n: "Signature Logo", p: "$150", f: ["2 custom logo concepts", "2 revisions", "High-resolution PNG"] },
      { n: "Premium Logo", p: "$250", f: ["2 detailed custom logo concepts", "3 revisions", "Alternate logo version", "High-resolution PNG"] },
      { n: "Luxury Logo Collection", p: "$450", f: ["3 custom logo concepts", "4 revisions", "Multiple logo variations", "Brand color palette", "High-resolution PNG"] },
    ],
  },
  {
    title: "Logo Enhancements & Add-ons",
    desc: "Already have a logo? We can refresh it with a cleaner 2D update or turn it into a polished 3D version.",
    cta: "Brand kit add-on",
    items: [
      { n: "Brand Kit Add-on", p: "$150" },
      { n: "Logo Refresh (2D Update)", p: "$75" },
      { n: "3D Logo Upgrade", p: "$125" },
    ],
  },
  {
    title: "Print & Marketing",
    desc: "Flyers, business cards, menus, and brochures that bring in customers.",
    items: [
      { n: "Business cards (two-sided)", p: "$100" },
      { n: "Flyer (one-sided)", p: "$125" },
      { n: "Price sheet or menu", p: "$125" },
      { n: "Brochure (tri-fold)", p: "$200" },
    ],
  },
  {
    title: "Business Documents",
    desc: "Branded, editable templates that look professional every time you use them.",
    items: [{ n: "Invoice or form template", p: "$100 each" }],
  },
  {
    title: "Social Media Design",
    desc: "One-time graphics for your page. Ongoing management is offered monthly below.",
    items: [
      { n: "Profile & cover set", p: "$100" },
      { n: "5 social graphics", p: "$175" },
      { n: "10 social graphics", p: "$300" },
    ],
  },
  {
    title: "Websites",
    desc: "Refresh the site you already have, or start with a brand-new one.",
    items: [
      { n: "Website Refresh (up to 3 pages)", p: "from $350" },
      { n: "Full Website Refresh (up to 5 pages)", p: "from $650" },
      { n: "New Website (up to 5 pages)", p: "from $1,400" },
    ],
  },
  {
    title: "Promotional Videos",
    desc: "Short videos and reels that showcase your business.",
    items: [
      { n: "Promo video (15–30 sec)", p: "$150" },
      { n: "Extended promo video (60+ sec)", p: "from $250" },
    ],
  },
  {
    title: "Grant Support",
    desc: "Grant-ready portfolios, grant research, and application help.",
    items: [{ n: "See grant services", p: "from $100" }],
  },
];

const PACKAGES = [
  {
    tier: "BRONZE",
    name: "Brand Starter",
    price: "$500",
    value: "$550 value",
    features: ["Logo (2 concepts)", "Business cards", "Facebook profile & cover set"],
    delivery: "Delivered in about 2 weeks",
  },
  {
    tier: "SILVER",
    name: "Brand Builder",
    price: "$1,000",
    value: "$1,125 value",
    popular: true,
    features: ["Everything in Bronze, plus:", "Brand kit (colors, fonts, usage guide)", "1 flyer", "Price sheet or menu", "5 social media graphics"],
    delivery: "Delivered in about 3 weeks",
  },
  {
    tier: "GOLD",
    name: "Business Launch",
    price: "$1,800",
    value: "$2,525 value",
    features: ["Everything in Silver, plus:", "Website (up to 5 pages)"],
    delivery: "Delivered in about 4 weeks",
  },
];

const MONTHLY_SOCIAL = [
  { name: "Essentials", price: "$400/month", items: ["8 posts", "1 platform", "Captions and scheduling", "Monthly summary"] },
  { name: "Growth", price: "$750/month", items: ["12 posts", "2 platforms", "2 short videos created from client-provided footage", "Captions and scheduling", "Monthly summary"] },
  { name: "Premium", price: "$1,100/month", items: ["16 posts", "Up to 3 platforms", "4 short videos created from client-provided footage", "Strategy call", "Monthly summary"] },
];

const MONTHLY_TERMS = "3-month minimum. Billed monthly in advance. Ad spend, comment replies, and daily monitoring are not included.";

const GRANT_SERVICES = [
  { n: "Grant-Ready Portfolio", p: "$550", inc: ["One-page business summary", "Branded business plan layout", "Capability statement", "Simple budget sheet", "Organized document checklist"] },
  { n: "Grant Research", p: "$100", inc: ["Researched list of grant opportunities that fit the business", "Eligibility notes", "Deadlines"] },
  { n: "Application Support", p: "from $400", inc: ["Help preparing, writing, and organizing answers and attachments for ONE grant application"] },
];

const GRANT_DISCLAIMER = "We do not guarantee grant approval or funding. Our fees cover the work, research, and deliverables we provide and are the same whether or not a grant is awarded. Funding decisions are made solely by each grantor.";

const STEPS = ["Discovery", "Concept", "Revisions", "Delivery"];

const GOOD_TO_KNOW = [
  { label: "Revisions", text: "Up to 2 revision rounds are included. Logos include 2 concepts. Additional revision rounds are $50 each." },
  { label: "Payment", text: "50% deposit required to begin. Remaining balance due before final files are delivered. Projects under $200 must be paid in full upfront." },
  { label: "Turnaround (approximate)", text: "Social graphics, business cards, and templates: 2–3 business days. Flyers and price sheets: 3–5 business days. Brochures and promotional videos: 5–7 business days. Logos: 7–10 business days. Websites: 3–4 weeks. The turnaround clock begins after the required payment/deposit AND project information have been received." },
  { label: "Rush service", text: "+50% when scheduling allows." },
  { label: "Final files", text: "Print-ready PDF and/or web-ready files where appropriate. Logo files may include PNG, JPG, PDF, and SVG. Printing, domain registration, and hosting are separate unless specifically included in a service." },
];

const FAQS = [
  { q: "How do I get started?", a: "Send a quote request through the contact form, or call or email us. We'll reply with next steps and a simple quote." },
  { q: "How much is required to begin a project?", a: "A 50% deposit starts your project, and the balance is due before final files are delivered. Projects under $200 are paid in full upfront." },
  { q: "How many revisions are included?", a: "Up to 2 revision rounds are included on every project, and logos include 2 concepts. Additional rounds are $50 each." },
  { q: "How long does a project take?", a: "Social graphics, business cards, and templates take about 2–3 business days; flyers and price sheets 3–5; brochures and promotional videos 5–7; logos 7–10; and websites about 3–4 weeks. Timelines begin once your deposit and project information are received." },
  { q: "Do you offer rush service?", a: "Yes, when scheduling allows, for an additional 50%." },
  { q: "Are printing, domain, and hosting fees included?", a: "No. Printing, domain registration, and hosting are separate unless specifically included in a service." },
  { q: "Do you guarantee grant approval?", a: "No. We do not guarantee grant approval or funding. Our fees cover the work, research, and deliverables we provide and are the same whether or not a grant is awarded. Funding decisions are made solely by each grantor." },
  { q: "What happens after I submit a quote request?", a: "We reply within 1–2 business days with next steps and a simple quote. For website refreshes, we review your current site before you pay anything." },
];

// ─── Lightbox ──────────────────────────────────────────────
function Lightbox({ open, images, index, onClose, onPrev, onNext }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, onPrev, onNext]);

  if (!open) return null;
  const img = images[index];
  return (
    <div
      data-testid="lightbox"
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 md:p-10"
      onClick={onClose}
    >
      <button data-testid="lightbox-close" onClick={onClose} className="absolute top-4 right-4 md:top-6 md:right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center z-10">
        <X size={22} />
      </button>
      <button data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); onPrev(); }} className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center z-10">
        <ChevronLeft size={24} />
      </button>
      <button data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); onNext(); }} className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center z-10">
        <ChevronRight size={24} />
      </button>
      <figure className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
        <img src={img.img} alt={img.title} className="w-full max-h-[78vh] object-contain rounded-md" />
        <figcaption className="text-center text-white/80 mt-4 text-sm">
          <p className="font-serif text-xl text-copper-200">{img.title}</p>
          <p className="mt-1">{img.caption}</p>
          <p className="mt-2 text-white/50 text-xs">{index + 1} / {images.length}</p>
        </figcaption>
      </figure>
    </div>
  );
}

// ─── Scroll + prefill helper ───────────────────────────────
const scrollTo = (id, prefill) => {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  if (prefill) {
    setTimeout(() => {
      const sel = document.querySelector('[data-testid="contact-service"]');
      if (!sel) return;
      const options = Array.from(sel.options);
      const needle = prefill.toLowerCase();
      let match = options.find((o) => o.value.toLowerCase() === needle);
      if (!match) match = options.find((o) => o.value.toLowerCase().includes(needle));
      if (!match) {
        const first = needle.split(/[\s—-]+/)[0];
        match = options.find((o) => o.value.toLowerCase().includes(first));
      }
      if (match) {
        const native = Object.getOwnPropertyDescriptor(window.HTMLSelectElement.prototype, "value").set;
        native.call(sel, match.value);
        sel.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }, 600);
  }
};

// ─── Shared bits ───────────────────────────────────────────
const Eyebrow = ({ children, dark = false }) => (
  <p className={`text-[11px] uppercase tracking-[0.4em] font-semibold mb-5 ${dark ? "text-copper-300" : "text-copper-600"}`}>{children}</p>
);

// ─── Our Portfolio (client's actual portfolio pages) ───────
const PORTFOLIO_PAGES = [
  { img: "/assets/portfolio-1.jpg", title: "Logo Design", caption: "One name. Multiple styles." },
  { img: "/assets/portfolio-2.jpg", title: "Restaurant Marketing & Design", caption: "Flavor House Restaurant" },
  { img: "/assets/portfolio-3.jpg", title: "Brand Transformation", caption: "Flavor House Restaurant" },
  { img: "/assets/portfolio-4.jpg", title: "Website Redesign", caption: "Carter Realty Group" },
  { img: "/assets/portfolio-5.jpg", title: "Brand Identity & Marketing", caption: "Prestige Remodeling & Construction" },
  { img: "/assets/portfolio-6.jpg", title: "Tri-Fold Brochure", caption: "Nova Luxe Beauty Studio" },
  { img: "/assets/portfolio-7.jpg", title: "Business Documents", caption: "Carter & Associates Law Firm" },
  { img: "/assets/portfolio-8.jpg", title: "Grant-Ready Business Support", caption: "Prime Route Logistics LLC" },
  { img: "/assets/portfolio-9.jpg", title: "Social Media Suite", caption: "Legacy Shield Insurance Solutions" },
  { img: "/assets/portfolio-10.jpg", title: "Website Design", caption: "Elan Aesthetics" },
];

function PortfolioCarousel({ onOpen }) {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);

  const scrollToSlide = (i) => {
    const track = trackRef.current;
    const slide = track?.children[i];
    if (!track || !slide) return;
    track.scrollTo({ left: slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2, behavior: "smooth" });
  };

  const onScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const center = track.scrollLeft + track.clientWidth / 2;
    let best = 0, dist = Infinity;
    Array.from(track.children).forEach((s, i) => {
      const d = Math.abs(s.offsetLeft + s.clientWidth / 2 - center);
      if (d < dist) { dist = d; best = i; }
    });
    if (best !== active) setActive(best);
  };

  return (
    <div className="relative mt-12">
      <div
        ref={trackRef}
        onScroll={onScroll}
        data-testid="portfolio-carousel"
        className="no-scrollbar flex gap-5 md:gap-8 overflow-x-auto snap-x snap-mandatory px-[14%] md:px-[30%] py-6"
      >
        {PORTFOLIO_PAGES.map((p, i) => (
          <button
            key={p.img}
            onClick={() => onOpen(i)}
            data-testid={`portfolio-page-${i}`}
            aria-label={`View ${p.title} full screen`}
            className={`snap-center shrink-0 w-[72%] sm:w-[46%] md:w-[340px] lg:w-[380px] transition-all duration-300 ${i === active ? "opacity-100 scale-100" : "opacity-45 scale-[0.93]"}`}
          >
            <img src={p.img} alt={p.title} className="w-full h-auto rounded-md shadow-2xl bg-white pointer-events-none" />
          </button>
        ))}
      </div>
      <button onClick={() => scrollToSlide(Math.max(0, active - 1))} data-testid="portfolio-prev" aria-label="Previous portfolio page"
        className="absolute left-3 md:left-10 top-[42%] -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-[#1B1411] text-white flex items-center justify-center shadow-lg hover:bg-copper-600 transition-colors z-10">
        <ChevronLeft size={22} />
      </button>
      <button onClick={() => scrollToSlide(Math.min(PORTFOLIO_PAGES.length - 1, active + 1))} data-testid="portfolio-next" aria-label="Next portfolio page"
        className="absolute right-3 md:right-10 top-[42%] -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-[#1B1411] text-white flex items-center justify-center shadow-lg hover:bg-copper-600 transition-colors z-10">
        <ChevronRight size={22} />
      </button>
      <div className="flex justify-center gap-2 mt-2" data-testid="portfolio-dots">
        {PORTFOLIO_PAGES.map((_, i) => (
          <button key={i} onClick={() => scrollToSlide(i)} aria-label={`Go to portfolio page ${i + 1}`} data-testid={`portfolio-dot-${i}`}
            className={`h-2 rounded-full transition-all ${i === active ? "w-6 bg-copper-500" : "w-2 bg-copper/30"}`} />
        ))}
      </div>
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────
export default function HomePage() {
  const [lightOpen, setLightOpen] = useState(false);
  const [lightIndex, setLightIndex] = useState(0);
  const [pfOpen, setPfOpen] = useState(false);
  const [pfIndex, setPfIndex] = useState(0);

  const openLight = useCallback((i) => { setLightIndex(i); setLightOpen(true); }, []);
  const openPf = useCallback((i) => { setPfIndex(i); setPfOpen(true); }, []);
  const nextPf = useCallback(() => setPfIndex((i) => (i + 1) % PORTFOLIO_PAGES.length), []);
  const prevPf = useCallback(() => setPfIndex((i) => (i - 1 + PORTFOLIO_PAGES.length) % PORTFOLIO_PAGES.length), []);
  const nextLight = useCallback(() => setLightIndex((i) => (i + 1) % GALLERY.length), []);
  const prevLight = useCallback(() => setLightIndex((i) => (i - 1 + GALLERY.length) % GALLERY.length), []);

  return (
    <div data-testid="home-page" className="bg-cream-50 text-ink-900">

      {/* ── HERO ───────────────────────────── */}
      <section id="top" data-testid="hero" className="relative overflow-hidden bg-[#171210]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_15%,rgba(199,147,105,0.14),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_10%_90%,rgba(199,147,105,0.10),transparent_55%)]" />
        <div className="relative max-w-[1200px] mx-auto px-6 md:px-10 pt-16 pb-20 md:pt-24 md:pb-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <Eyebrow dark>Branding · Design · Marketing</Eyebrow>
              <h1 className="font-serif font-medium text-white text-[2.55rem] sm:text-6xl lg:text-7xl leading-[1.08]">
                Look established.<br />Get noticed.<br />Get hired.
              </h1>
              <p className="mt-7 max-w-xl text-white/80 text-base md:text-lg leading-relaxed">
                Logos, flyers, websites, social media, video, and grant-ready portfolios for small business owners who are ready to look as professional as they are.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-4">
                <button onClick={() => scrollTo("contact")} data-testid="hero-cta-quote" className="btn-copper flex-1 sm:flex-none">GET A FREE QUOTE</button>
                <button onClick={() => scrollTo("featured")} data-testid="hero-cta-work" className="btn-copper-outline flex-1 sm:flex-none">SEE OUR WORK</button>
              </div>
              <p className="mt-10 font-script text-copper-300 text-3xl md:text-4xl hidden lg:block">Your Vision. Our Signature.</p>
            </div>
            <div className="lg:pl-4">
              <img
                src={HERO_IMG}
                alt="Toliver Signature Services brand showcase"
                data-testid="hero-image"
                className="w-full h-auto rounded-xl shadow-2xl shadow-black/50 ring-1 ring-copper/25"
              />
            </div>
            <p className="mt-10 font-script text-copper-300 text-3xl lg:hidden">Your Vision. Our Signature.</p>
          </div>
        </div>
      </section>

      {/* ── BUSINESS ASSISTANCE ────────────── */}
      <BusinessAssistancePromo />

      {/* ── SERVICES & PRICING ─────────────── */}
      <section id="services" data-testid="services" className="py-20 md:py-24 bg-[#F2E8DE] scroll-mt-20 md:scroll-mt-24">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="text-center mb-14">
            <Eyebrow>Services &amp; Pricing</Eyebrow>
            <h2 className="font-serif font-medium text-4xl md:text-5xl text-ink-900 leading-tight">Everything your business<br className="hidden md:block" /> needs to look the part.</h2>
            <p className="text-ink-800/75 mt-5 max-w-2xl mx-auto">Need just one thing? Every service is available on its own.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {SERVICE_CATEGORIES.map((cat, i) => (
              <div key={cat.title} data-testid={`service-cat-${i}`} className="p-7 md:p-8 bg-white rounded-xl shadow-[0_2px_16px_rgba(20,16,12,0.06)]">
                <h3 className="font-serif font-bold text-xl md:text-2xl">{cat.title}</h3>
                <p className="text-sm text-ink-800/70 mt-2">{cat.desc}</p>
                <div className="border-t border-copper/15 mt-5 pt-5">
                  <ul className="space-y-3.5">
                    {cat.items.map((it) => (
                      <li key={it.n} className="flex items-baseline justify-between gap-4 text-[15px]">
                        <span className="text-ink-900">{it.n}
                          {it.f && <span className="block text-sm font-normal text-ink-800/60 mt-1 leading-relaxed">{it.f.join(" · ")}</span>}
                        </span>
                        <span className="font-serif font-bold text-copper-600 whitespace-nowrap">{it.p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <button onClick={() => scrollTo("contact", cat.cta || cat.title)} data-testid={`service-cat-cta-${i}`} className="mt-6 text-[11px] uppercase tracking-[0.22em] font-semibold text-copper-700 hover:text-copper-500 inline-flex items-center gap-2">
                  Request This <ArrowRight size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURED WORK ──────────────────── */}
      <section id="featured" data-testid="featured" className="py-20 md:py-24 bg-cream-50 scroll-mt-20 md:scroll-mt-24">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <Eyebrow>Featured Work</Eyebrow>
            <h2 className="font-serif font-medium text-4xl md:text-5xl text-ink-900 leading-tight">The Toliver Signature<br className="hidden md:block" /> Collection.</h2>
          </div>

          {/* Before / After */}
          <div className="max-w-3xl mx-auto">
            <button onClick={() => openLight(0)} data-testid="before-card" className="block w-full group" aria-label="View Before — Original Facebook Branding full size">
              <img src={GALLERY[0].img} alt="Before — Original Facebook Branding" className="w-full h-auto rounded-2xl shadow-lg group-hover:opacity-95 transition-opacity" />
            </button>

            <div className="flex justify-center -my-5 relative z-10">
              <span className="w-12 h-12 rounded-full bg-gradient-to-br from-copper-200 to-copper-500 shadow-lg shadow-copper/40 flex items-center justify-center">
                <ChevronDown size={20} className="text-[#1C1310]" strokeWidth={2.5} />
              </span>
            </div>

            <button onClick={() => openLight(1)} data-testid="after-card" className="block w-full group" aria-label="View After — Refreshed Facebook Branding full size">
              <img src={GALLERY[1].img} alt="After — Refreshed Facebook Branding" className="w-full h-auto rounded-2xl shadow-lg group-hover:opacity-95 transition-opacity" />
            </button>

            <div className="text-center mt-12 mb-14">
              <p className="font-serif font-medium text-2xl md:text-3xl tracking-[0.14em] text-ink-900">TOLIVER SIGNATURE SERVICES</p>
              <p className="font-script text-copper-500 text-4xl md:text-5xl mt-2">Facebook Brand Refresh</p>
              <p className="text-ink-800 tracking-[0.3em] text-[11px] uppercase font-semibold mt-4">Same Business. Refined Identity.</p>
            </div>
          </div>

          {/* Portfolio grid */}
          <div className="grid grid-cols-2 gap-4 md:gap-7 max-w-4xl mx-auto">
            {PORTFOLIO.map((f, i) => (
              <div key={f.title}>
                <button onClick={() => openLight(i + 2)} data-testid={`portfolio-${i}`} className="block w-full group">
                  <img src={f.img} alt={f.title} className="w-full rounded-lg shadow-md group-hover:shadow-xl group-hover:scale-[1.015] transition-all duration-300" />
                </button>
                <p className="mt-3 text-ink-900 text-base md:text-lg">{f.title}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-ink-800/70 text-sm mt-10">Tap any image to view it full size.</p>
        </div>
      </section>

      {/* ── OUR PORTFOLIO ────────────────── */}
      <section id="portfolio" data-testid="our-portfolio" className="py-20 md:py-24 bg-[#F2E8DE] overflow-hidden scroll-mt-20 md:scroll-mt-24">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <Eyebrow>Our Portfolio</Eyebrow>
          <h2 className="font-serif font-medium text-5xl md:text-6xl text-ink-900 leading-tight">Our Portfolio</h2>
          <p className="text-ink-800/75 mt-5 max-w-xl leading-relaxed">Explore examples of our branding, websites, marketing materials, social media, business documents, grant support and more.</p>
        </div>
        <PortfolioCarousel onOpen={openPf} />
        <p className="text-center text-ink-800/70 text-sm mt-8 px-6">Swipe or use the arrows to browse. Tap a page to view it full screen.</p>
        <div className="text-center mt-8 px-6">
          <button onClick={() => openPf(0)} data-testid="view-full-portfolio" className="bg-[#1B1411] text-white uppercase tracking-[0.25em] font-bold text-sm px-12 py-5 rounded-xl hover:bg-copper-700 transition-colors w-full sm:w-auto">VIEW FULL PORTFOLIO</button>
        </div>
      </section>

      {/* ── BRANDING PACKAGES ──────────────── */}
      <section id="packages" data-testid="packages" className="py-20 md:py-24 bg-[#171210] text-white scroll-mt-20 md:scroll-mt-24">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="text-center mb-14">
            <Eyebrow dark>Branding Packages</Eyebrow>
            <h2 className="font-serif font-medium text-4xl md:text-5xl leading-tight">Get more done, for less.</h2>
            <p className="text-white/70 mt-5 max-w-xl mx-auto">One-time packages that cost less than buying the same pieces separately.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {PACKAGES.map((p) => (
              <div key={p.tier} data-testid={`pkg-${p.tier.toLowerCase()}`} className={`relative rounded-xl border bg-[#1E1613] ${p.popular ? "border-copper-300 shadow-[0_0_50px_rgba(199,147,105,0.18)]" : "border-copper/20"}`}>
                {p.popular && <span className="absolute top-5 right-5 text-[9px] uppercase tracking-[0.22em] bg-gradient-to-br from-copper-200 to-copper-500 text-[#1C1310] px-4 py-1.5 rounded-full font-bold">Most Popular</span>}
                <div className="p-8">
                  <p className="text-copper-300 tracking-[0.35em] text-xs font-semibold">{p.tier}</p>
                  <p className="font-serif font-medium text-white text-3xl mt-3">{p.name}</p>
                  <p className="mt-5 flex items-baseline gap-3">
                    <span className="font-serif font-medium text-5xl text-white">{p.price}</span>
                    <span className="text-sm text-white/50">{p.value}</span>
                  </p>
                  <ul className="mt-7 space-y-3">
                    {p.features.map((f) => (
                      <li key={f} className="text-[15px] text-white/85">{f}</li>
                    ))}
                  </ul>
                  <p className="mt-7 text-sm text-white/55">{p.delivery}</p>
                  {p.popular ? (
                    <button onClick={() => scrollTo("contact", `${p.tier} ${p.name} Package`)} data-testid={`pkg-cta-${p.tier.toLowerCase()}`} className="btn-copper mt-7 w-full">CHOOSE {p.tier}</button>
                  ) : (
                    <button onClick={() => scrollTo("contact", `${p.tier} ${p.name} Package`)} data-testid={`pkg-cta-${p.tier.toLowerCase()}`} className="btn-copper-outline mt-7 w-full">CHOOSE {p.tier}</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WEBSITES ───────────────────────── */}
      <section id="websites" data-testid="websites" className="py-20 md:py-24 bg-cream-50 scroll-mt-20 md:scroll-mt-24">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <Eyebrow>Websites</Eyebrow>
            <h2 className="font-serif font-medium text-4xl md:text-5xl text-ink-900 leading-tight">Refresh what you have,<br className="hidden md:block" /> or start fresh.</h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            {/* Refresh vs New intro cards */}
            <div className="grid grid-cols-2 gap-4 md:gap-6">
              <div data-testid="web-intro-refresh" className="bg-[#F3EADB] rounded-xl p-6 md:p-7">
                <p className="text-copper-700 tracking-[0.3em] text-[11px] uppercase font-bold">Refresh</p>
                <p className="text-sm text-ink-800/85 mt-3 leading-relaxed">Your site works. It just looks outdated. Same platform, same pages, a newer look.</p>
              </div>
              <div data-testid="web-intro-new" className="bg-[#1B1411] rounded-xl p-6 md:p-7">
                <p className="text-copper-300 tracking-[0.3em] text-[11px] uppercase font-bold">New Website</p>
                <p className="text-sm text-white/80 mt-3 leading-relaxed">You have no site, or yours needs new pages, features, or structure.</p>
              </div>
            </div>

            {/* Website Before & After example (temporary demo content) */}
            <div data-testid="placeholder-before-after-site" className="space-y-4 md:space-y-6">
              <button onClick={() => openLight(8)} data-testid="site-before-img" className="block w-full group" aria-label="View Before — original website design full size">
                <img src={GALLERY[8].img} alt="Before — original Curley J's Products website design" className="w-full h-auto rounded-xl shadow-md group-hover:shadow-xl transition-shadow" />
              </button>
              <button onClick={() => openLight(9)} data-testid="site-after-img" className="block w-full group" aria-label="View After — refreshed website design full size">
                <img src={GALLERY[9].img} alt="After — refreshed Curley J's Products website design" className="w-full h-auto rounded-xl shadow-md group-hover:shadow-xl transition-shadow" />
              </button>
            </div>

            {/* Website Refresh card */}
            <div data-testid="website-refresh" className="bg-white rounded-xl border border-copper/15 shadow-[0_2px_16px_rgba(20,16,12,0.06)] p-7 md:p-9">
              <h3 className="font-serif font-medium text-3xl">Website Refresh</h3>
              <ul className="mt-5 space-y-3 text-[15px]">
                <li className="flex items-baseline justify-between gap-4"><span>Refresh, up to 3 existing pages</span><span className="font-serif font-bold text-copper-600 whitespace-nowrap">from $350</span></li>
                <li className="flex items-baseline justify-between gap-4"><span>Full Refresh, up to 5 existing pages</span><span className="font-serif font-bold text-copper-600 whitespace-nowrap">from $650</span></li>
              </ul>
              <div className="border-t border-copper/15 mt-6 pt-6">
                <p className="font-bold text-[15px]">What's included</p>
                <ul className="mt-3 space-y-2.5 text-[15px] text-ink-800/85">
                  {["Updated colors and fonts to match your brand", "Cleaner layout for your existing sections", "Polished headlines and button wording", "Your logo and photos swapped in", "Mobile display check and fixes", "Contact links and buttons checked"].map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>
                <p className="font-bold text-[15px] mt-6">Revisions &amp; timing</p>
                <p className="mt-2 text-[15px] text-ink-800/85">2 revision rounds included, extra rounds $50 each. About 5–7 business days for a Refresh, 7–10 for a Full Refresh.</p>
                <p className="font-bold text-[15px] mt-6">What we need from you</p>
                <p className="mt-2 text-[15px] text-ink-800/85">Login access to your current website, plus any new photos or wording you want used.</p>
              </div>
              <button onClick={() => scrollTo("contact", "Website Refresh")} data-testid="refresh-cta" className="btn-copper-outline !text-copper-700 !border-copper-500/70 hover:!bg-copper-50 mt-7">REQUEST A REVIEW <ArrowRight size={14} /></button>
            </div>

            {/* Custom quote conditions */}
            <div data-testid="website-custom-quote" className="bg-[#F3EADB] rounded-xl p-7 md:p-9">
              <p className="font-bold text-[15px]">A refresh becomes a custom quote if you need:</p>
              <ul className="mt-4 space-y-2.5 text-[15px] text-ink-800/85">
                {["A move to a different platform or host", "New pages added (extra existing pages are $100 each)", "Online store, booking, memberships, or other new features", "All-new written content for your pages", "Navigation or page structure rebuilt from scratch", "Repairs to a broken site, or no login access"].map((x) => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
              <p className="mt-5 text-[15px] text-ink-800/85">We review your site before you pay anything. If it needs more than a refresh, we'll tell you upfront and quote a new website instead.</p>
            </div>

            {/* New Website dark card */}
            <div data-testid="website-new" className="bg-[#1B1411] rounded-xl p-7 md:p-9 text-white">
              <button onClick={() => openLight(10)} data-testid="new-site-img" className="block w-full group" aria-label="View new website example full size">
                <img src={GALLERY[10].img} alt="Example of a new website designed from scratch — Maison Luxe Events, shown on a laptop and phone" className="w-full h-auto rounded-lg group-hover:opacity-95 transition-opacity" />
              </button>
              <div className="mt-7 flex items-baseline justify-between gap-4 flex-wrap">
                <h3 className="font-serif font-medium text-3xl">New Website</h3>
                <span className="font-serif font-bold text-copper-300 text-2xl whitespace-nowrap">from $1,400</span>
              </div>
              <p className="mt-3 text-white/80 text-[15px]">Up to 5 pages designed from scratch — modern layout, mobile-ready, built around your brand.</p>
              <button onClick={() => scrollTo("contact", "New Website")} data-testid="newsite-cta" className="btn-copper mt-7">START A NEW SITE <ArrowRight size={14} /></button>
            </div>
          </div>
        </div>
      </section>

      {/* ── MONTHLY SOCIAL MEDIA ───────────── */}
      <section id="monthly" data-testid="monthly" className="py-20 md:py-24 bg-[#171210] text-white scroll-mt-20 md:scroll-mt-24">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="text-center mb-14">
            <Eyebrow dark>Monthly Social Media</Eyebrow>
            <h2 className="font-serif font-medium text-4xl md:text-5xl leading-tight">Keep showing up — consistently.</h2>
            <p className="text-white/70 mt-5 max-w-xl mx-auto">Ongoing management plans that keep your pages active, branded, and growing.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {MONTHLY_SOCIAL.map((m) => (
              <div key={m.name} data-testid={`monthly-${m.name.toLowerCase()}`} className="rounded-xl border border-copper/20 bg-[#1E1613] p-8">
                <p className="text-copper-300 tracking-[0.35em] text-xs font-semibold">{m.name.toUpperCase()}</p>
                <p className="font-serif font-medium text-white text-3xl mt-3">{m.price}</p>
                <ul className="mt-6 space-y-3">
                  {m.items.map((it) => <li key={it} className="flex items-start gap-2.5 text-[15px] text-white/85"><Check size={14} className="text-copper-300 mt-1 shrink-0" />{it}</li>)}
                </ul>
                <button onClick={() => scrollTo("contact", `Monthly Social — ${m.name}`)} data-testid={`monthly-cta-${m.name.toLowerCase()}`} className="btn-copper-outline mt-7 w-full">GET A QUOTE</button>
              </div>
            ))}
          </div>
          <p data-testid="monthly-terms" className="text-center text-white/60 text-sm mt-8 max-w-2xl mx-auto">{MONTHLY_TERMS}</p>
        </div>
      </section>

      {/* ── GRANT SUPPORT ──────────────────── */}
      <section id="grants" data-testid="grants" className="py-20 md:py-24 bg-[#F2E8DE] scroll-mt-20 md:scroll-mt-24">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <Eyebrow>Grant Support</Eyebrow>
            <h2 className="font-serif font-medium text-4xl md:text-5xl text-ink-900 leading-tight">Grant-ready. Clearly presented.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {GRANT_SERVICES.map((g, i) => (
              <div key={g.n} data-testid={`grant-${i}`} className="bg-white rounded-xl shadow-[0_2px_16px_rgba(20,16,12,0.06)] p-7 md:p-8 flex flex-col">
                <h3 className="font-serif font-bold text-xl">{g.n}</h3>
                <p className="font-serif font-bold text-copper-600 text-3xl mt-3">{g.p}</p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-copper-700 font-bold mt-5 mb-2">Includes</p>
                <ul className="space-y-2.5 text-sm text-ink-800/85">
                  {g.inc.map((x) => <li key={x} className="flex items-start gap-2.5"><Check size={14} className="text-copper-500 mt-0.5 shrink-0" />{x}</li>)}
                </ul>
                <button onClick={() => scrollTo("contact", "Grant Support")} data-testid={`grant-cta-${i}`} className="mt-auto pt-6 text-[11px] uppercase tracking-[0.22em] font-semibold text-copper-700 hover:text-copper-500 inline-flex items-center gap-2">Request This <ArrowRight size={12} /></button>
              </div>
            ))}
          </div>
          <div className="max-w-5xl mx-auto mt-6 bg-[#1B1411] text-white rounded-xl p-7 md:p-9 flex items-start gap-5">
            <ShieldCheck size={26} className="text-copper-300 shrink-0 mt-1" />
            <div>
              <p className="text-copper-300 tracking-[0.3em] text-[11px] uppercase font-bold">Important</p>
              <p className="mt-3 text-sm leading-relaxed text-white/85">{GRANT_DISCLAIMER}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS / GOOD TO KNOW ────── */}
      <section id="how-it-works" data-testid="how-it-works" className="py-20 md:py-24 bg-cream-50 scroll-mt-20 md:scroll-mt-24">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <Eyebrow>How It Works</Eyebrow>
            <h2 className="font-serif font-medium text-4xl md:text-5xl text-ink-900 leading-tight">A simple, clear process.</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-4xl mx-auto">
            {STEPS.map((s, i) => (
              <div key={s} data-testid={`step-${i}`} className="bg-white rounded-xl border border-copper/15 p-6 md:p-7 text-center shadow-[0_2px_16px_rgba(20,16,12,0.06)]">
                <p className="font-serif font-bold text-copper-500 text-3xl">{i + 1}</p>
                <p className="mt-2 font-semibold tracking-[0.12em] uppercase text-sm text-ink-900">{s}</p>
              </div>
            ))}
          </div>
          <div className="max-w-4xl mx-auto mt-10 bg-white rounded-xl border border-copper/15 shadow-[0_2px_16px_rgba(20,16,12,0.06)] p-7 md:p-9" data-testid="good-to-know">
            <h3 className="font-serif font-bold text-2xl">Good to Know</h3>
            <ul className="mt-5 space-y-4 text-[15px] text-ink-800/85 leading-relaxed">
              {GOOD_TO_KNOW.map((g) => (
                <li key={g.label}><strong className="text-ink-900">{g.label}:</strong> {g.text}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── ABOUT THE STUDIO ───────────────── */}
      <section id="about" data-testid="about" className="py-20 md:py-24 bg-[#171210] text-white scroll-mt-20 md:scroll-mt-24">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-4 flex justify-center lg:justify-start">
            <img src={ABOUT_LOGO} alt="Toliver Signature Services" data-testid="about-logo" className="w-52 h-52 md:w-72 md:h-72 rounded-full ring-1 ring-copper/40 object-cover shadow-2xl shadow-black/50" />
          </div>
          <div className="lg:col-span-8 text-center lg:text-left">
            <Eyebrow dark>About the Studio</Eyebrow>
            <h2 className="font-serif font-medium text-4xl md:text-5xl leading-tight">A small studio that treats your business like it matters.</h2>
            <p className="mt-6 text-white/80 text-lg leading-relaxed max-w-2xl mx-auto lg:mx-0">Toliver Signature Services helps small business owners look professional, stay consistent, and get ready for growth, from a first logo to a full business launch.</p>
            <p className="mt-8 font-script text-copper-300 text-3xl md:text-4xl">Your Vision. Our Signature.</p>
          </div>
        </div>
      </section>

      {/* ── FAQ ────────────────────────────── */}
      <section id="faq" data-testid="faq" className="py-20 md:py-24 bg-[#F2E8DE] scroll-mt-20 md:scroll-mt-24">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <Eyebrow>FAQ</Eyebrow>
            <h2 className="font-serif font-medium text-4xl md:text-5xl text-ink-900 leading-tight">Questions, answered.</h2>
          </div>
          <Accordion type="single" collapsible className="w-full space-y-4">
            {FAQS.map((f, i) => (
              <AccordionItem key={f.q} value={`q-${i}`} data-testid={`faq-${i}`} className="bg-white rounded-xl border border-copper/15 px-6 shadow-[0_2px_16px_rgba(20,16,12,0.06)] !border-b">
                <AccordionTrigger className="text-left font-semibold text-ink-900 py-5 hover:no-underline text-[15px]">{f.q}</AccordionTrigger>
                <AccordionContent className="text-ink-800/85 pb-5 text-[15px] leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ── START YOUR PROJECT CTA ─────────── */}
      <section data-testid="start-project-cta" className="bg-cream-50 border-t border-copper/15 py-14">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div>
            <Eyebrow>Already know what you need?</Eyebrow>
            <p className="text-ink-800/85 text-lg">Send your project details, files, and links and get started.</p>
          </div>
          <Link to="/start-your-project" data-testid="cta-start-project" className="btn-copper shrink-0">START YOUR PROJECT <ArrowRight size={14} /></Link>
        </div>
      </section>

      {/* ── CONTACT ────────────────────────── */}
      <ContactSection />

      {/* ── FOOTER ─────────────────────────── */}
      <SiteFooter />

      {/* Lightbox */}
      <Lightbox open={lightOpen} images={GALLERY} index={lightIndex} onClose={() => setLightOpen(false)} onPrev={prevLight} onNext={nextLight} />
      <Lightbox open={pfOpen} images={PORTFOLIO_PAGES} index={pfIndex} onClose={() => setPfOpen(false)} onPrev={prevPf} onNext={nextPf} />
    </div>
  );
}

// ─── Contact Section ───────────────────────────────────────
function ContactSection() {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", business: "", service: "", project_details: "" });
  const [status, setStatus] = useState({ loading: false, success: false, error: "" });

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const inputCls = "w-full bg-white border border-copper/25 rounded-[10px] px-4 py-3 text-ink-900 placeholder-ink-800/40 focus:border-copper-500 focus:outline-none";
  const label = "text-[10px] uppercase tracking-[0.25em] text-copper-700 mb-2 block font-semibold";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.service || !form.project_details) {
      setStatus({ loading: false, success: false, error: "Please fill in name, email, service, and project details." });
      return;
    }
    setStatus({ loading: true, success: false, error: "" });
    try {
      const axios = (await import("axios")).default;
      await axios.post(`${API}/bookings`, {
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
        service: form.service,
        package_tier: form.business,
        project_details: form.project_details,
      });
      setStatus({ loading: false, success: true, error: "" });
      setForm({ full_name: "", email: "", phone: "", business: "", service: "", project_details: "" });
    } catch (err) {
      setStatus({ loading: false, success: false, error: err?.response?.data?.detail || "Could not submit — please try again or email us directly." });
    }
  };

  const SERVICES = [
    "Essential Logo — $45", "Starter Logo — $85", "Signature Logo — $150", "Premium Logo — $250", "Luxury Logo Collection — $450",
    "Brand kit add-on — $150", "Logo refresh (2D update) — $75", "3D logo upgrade — $125",
    "Business cards (two-sided) — $100", "Flyer (one-sided) — $125", "Price sheet or menu — $125", "Brochure (tri-fold) — $200",
    "Invoice or form template — $100 each",
    "Social — Profile & cover set — $100", "Social — 5 graphics — $175", "Social — 10 graphics — $300",
    "Website Refresh (up to 3) — from $350", "Full Website Refresh — from $650", "New Website — from $1,400",
    "Promo video (15-30 sec) — $150", "Extended promo video (60+ sec) — from $250",
    "Grant Support", "BRONZE Brand Starter Package", "SILVER Brand Builder Package", "GOLD Business Launch Package",
    "Monthly Social — Essentials", "Monthly Social — Growth", "Monthly Social — Premium",
    "Not sure yet — please advise",
  ];

  return (
    <section id="contact" data-testid="contact" className="py-20 md:py-24 bg-[#171210] text-white scroll-mt-20 md:scroll-mt-24">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Eyebrow dark>Contact</Eyebrow>
          <h2 className="font-serif font-medium text-4xl md:text-5xl leading-tight">Get a Free Quote</h2>
          <p className="font-script text-copper-300 text-3xl mt-3">Your Vision. Our Signature.</p>
          <p className="mt-6 text-white/80 leading-relaxed">Tell us about your project and we'll reply within 1–2 business days with next steps and a simple quote.</p>
          <ul className="mt-9 space-y-4 text-sm">
            <li><a href="mailto:toliversignatureservices@gmail.com" data-testid="contact-email-link" className="inline-flex items-center gap-3 text-white hover:text-copper-300 break-all"><Mail size={16} className="text-copper-300 shrink-0" /> toliversignatureservices@gmail.com</a></li>
            <li><a href="tel:6629126357" data-testid="contact-phone-link" className="inline-flex items-center gap-3 text-white hover:text-copper-300"><Phone size={16} className="text-copper-300 shrink-0" /> 662-912-6357</a></li>
          </ul>
        </div>
        <form onSubmit={submit} data-testid="contact-form" className="lg:col-span-7 bg-cream-50 text-ink-900 rounded-xl p-7 md:p-10">
          {status.success ? (
            <div data-testid="contact-success" className="text-center py-10">
              <div className="w-16 h-16 rounded-full bg-copper-500 text-white mx-auto flex items-center justify-center"><Check size={28} /></div>
              <h3 className="font-serif font-medium text-3xl mt-5">Thank you!</h3>
              <p className="text-ink-800/80 mt-3">Your request was received. We'll reply within 1–2 business days.</p>
              <button onClick={() => setStatus({ loading: false, success: false, error: "" })} className="btn-copper mt-7">SEND ANOTHER REQUEST</button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-5">
              <div><label className={label}>Full Name *</label><input data-testid="contact-name" required value={form.full_name} onChange={upd("full_name")} className={inputCls} placeholder="Jane Doe" /></div>
              <div><label className={label}>Email *</label><input data-testid="contact-email" required type="email" value={form.email} onChange={upd("email")} className={inputCls} placeholder="you@business.com" /></div>
              <div><label className={label}>Phone</label><input data-testid="contact-phone" value={form.phone} onChange={upd("phone")} className={inputCls} placeholder="Optional" /></div>
              <div><label className={label}>Business Name</label><input data-testid="contact-business" value={form.business} onChange={upd("business")} className={inputCls} placeholder="Your business" /></div>
              <div className="md:col-span-2"><label className={label}>Service / Package *</label>
                <select data-testid="contact-service" required value={form.service} onChange={upd("service")} className={`${inputCls} cursor-pointer`}>
                  <option value="">Select a service</option>
                  {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="md:col-span-2"><label className={label}>Project Details *</label>
                <textarea data-testid="contact-details" required rows={5} value={form.project_details} onChange={upd("project_details")} className={`${inputCls} resize-none`} placeholder="Tell us about your brand, event, or promo." />
              </div>
              {status.error && <p data-testid="contact-error" className="md:col-span-2 text-sm text-red-600">{status.error}</p>}
              <div className="md:col-span-2">
                <button type="submit" data-testid="contact-submit" disabled={status.loading} className="btn-copper disabled:opacity-60">{status.loading ? "Sending…" : "SEND REQUEST"} <ArrowRight size={14} /></button>
                <p className="text-xs text-ink-800/60 mt-3">By submitting this form, you agree to our <Link to="/privacy" data-testid="contact-privacy-link" className="underline underline-offset-2 text-copper-700 hover:text-copper-500">Privacy Policy</Link>.</p>
              </div>
            </div>
          )}
        </form>
      </div>
    </section>
  );
}

// ─── Site Footer ───────────────────────────────────────────
function SiteFooter() {
  const QUICK = [
    { label: "Services", id: "services" }, { label: "Featured Work", id: "featured" },
    { label: "Packages", id: "packages" }, { label: "Websites", id: "websites" },
    { label: "Monthly Social", id: "monthly" }, { label: "Grants", id: "grants" },
    { label: "FAQ", id: "faq" }, { label: "Contact", id: "contact" },
  ];

  return (
    <footer data-testid="home-footer" className="bg-black text-white py-16">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5 flex items-start gap-5">
          <img src={LOGO} alt="Toliver Signature Services" data-testid="footer-logo" className="h-24 w-24 rounded-full ring-1 ring-copper/40 object-cover shrink-0" />
          <div>
            <p className="font-serif font-medium text-2xl tracking-[0.14em]">TOLIVER</p>
            <p className="text-copper-300 text-[10px] uppercase tracking-[0.32em] mt-1.5">Signature Services</p>
            <p className="mt-4 text-white/60 text-[11px] tracking-[0.25em] uppercase font-semibold">Branding · Design · Marketing</p>
            <p className="mt-5 font-script text-copper-300 text-3xl">Your Vision. Our Signature.</p>
          </div>
        </div>
        <div className="md:col-span-3">
          <p className="text-white/50 text-[11px] uppercase tracking-[0.28em] mb-5 font-semibold">Quick Links</p>
          <ul className="space-y-2.5 text-sm">
            {QUICK.map((q) => (
              <li key={q.id}><button onClick={() => document.getElementById(q.id)?.scrollIntoView({ behavior: "smooth" })} data-testid={`footer-${q.id}`} className="text-white/85 hover:text-copper-300">{q.label}</button></li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-4">
          <p className="text-white/50 text-[11px] uppercase tracking-[0.28em] mb-5 font-semibold">Contact</p>
          <ul className="space-y-3.5 text-sm">
            <li><a href="mailto:toliversignatureservices@gmail.com" data-testid="footer-email" className="inline-flex items-center gap-3 hover:text-copper-300 break-all"><Mail size={14} className="text-copper-300 shrink-0" /> toliversignatureservices@gmail.com</a></li>
            <li><a href="tel:6629126357" data-testid="footer-phone" className="inline-flex items-center gap-3 hover:text-copper-300"><Phone size={14} className="text-copper-300 shrink-0" /> 662-912-6357</a></li>
            <li className="text-white/60 text-xs pl-7">toliversignatureservices.com</li>
          </ul>
        </div>
      </div>
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 mt-12 pt-7 border-t border-white/10 flex flex-wrap justify-between items-center gap-4 text-xs text-white/50">
        <p>© {new Date().getFullYear()} Toliver Signature Services LLC. All rights reserved.</p>
        <p>
          <Link to="/terms" data-testid="footer-terms" className="underline underline-offset-2 hover:text-copper-300">Terms &amp; Policies</Link>
          <span className="mx-2 text-white/30">·</span>
          <Link to="/privacy" data-testid="footer-privacy" className="underline underline-offset-2 hover:text-copper-300">Privacy Policy</Link>
        </p>
      </div>
    </footer>
  );
}
